import { collapseExpandedComposerCursor } from "../../composer-logic";
import type { useComposerDraftStore } from "../../composerDraftStore";
import type { ChatComposerHandle } from "./ChatComposer";

type DraftStore = ReturnType<(typeof useComposerDraftStore)["getState"]>;

/** The composer owns what a cursor reset means; this borrows its shape. */
type CursorState = NonNullable<Parameters<ChatComposerHandle["resetCursorState"]>[0]>;

type Images = Parameters<DraftStore["addImages"]>[1];
type Files = Parameters<DraftStore["addFiles"]>[1];
type TerminalContexts = Parameters<DraftStore["setTerminalContexts"]>[1];
type PreviewAnnotations = Parameters<DraftStore["setPreviewAnnotations"]>[1];
type ReviewComments = Parameters<DraftStore["setReviewComments"]>[1];

/** What a send took out of the composer, kept so it can be put back. */
export interface OutgoingComposerDraft<DesignOperation> {
  readonly prompt: string;
  readonly images: Images;
  readonly files: Files;
  readonly terminalContexts: TerminalContexts;
  readonly previewAnnotations: PreviewAnnotations;
  readonly reviewComments: ReviewComments;
  /** A Design Operation the send was carrying, to be offered again. */
  readonly designOperation: DesignOperation | undefined;
}

/**
 * The draft as it goes back: the same content, with images that can be shown
 * again. Both the live composer and the persisted draft receive this.
 */
export interface RestoredComposerDraft {
  readonly prompt: string;
  readonly images: Images;
  readonly files: Files;
  readonly terminalContexts: TerminalContexts;
  readonly previewAnnotations: PreviewAnnotations;
  readonly reviewComments: ReviewComments;
}

export interface RestoreComposerDraftPorts<DesignOperation> {
  /** What the composer reads while the user is typing. */
  readonly writeLiveComposer: (draft: RestoredComposerDraft) => void;
  /** The persisted draft for the thread this send came from. */
  readonly writeDraft: (draft: RestoredComposerDraft) => void;
  readonly resetCursorState: (state: CursorState) => void;
  /** Offers a carried workspace operation again; an absent one stays absent. */
  readonly rememberPendingOperations: (operations: {
    readonly design: DesignOperation | undefined;
  }) => void;
  /** Makes an image usable again after its dispatch failed. */
  readonly cloneImageForRetry: (image: Images[number]) => Images[number];
}

/**
 * Put a consumed draft back after a send that never landed, so the user's
 * work is where they left it rather than lost to a failed dispatch.
 *
 * Images are cloned on the way back in: the originals were handed to a
 * dispatch that never landed and their preview URLs are already revoked, so
 * restoring them as they are would show the user broken previews.
 *
 * The cursor is collapsed onto the restored prompt: the expanded cursor the
 * composer had belonged to a draft that has since been rewritten, so carrying
 * it over would place the caret against text that no longer exists.
 */
export function restoreComposerDraftAfterFailedSend<DesignOperation>(
  outgoing: OutgoingComposerDraft<DesignOperation>,
  ports: RestoreComposerDraftPorts<DesignOperation>,
): void {
  const restored: RestoredComposerDraft = {
    prompt: outgoing.prompt,
    images: outgoing.images.map(ports.cloneImageForRetry),
    files: outgoing.files,
    terminalContexts: outgoing.terminalContexts,
    previewAnnotations: outgoing.previewAnnotations,
    reviewComments: outgoing.reviewComments,
  };

  ports.writeLiveComposer(restored);
  ports.writeDraft(restored);
  ports.rememberPendingOperations({ design: outgoing.designOperation });
  ports.resetCursorState({
    cursor: collapseExpandedComposerCursor(outgoing.prompt, outgoing.prompt.length),
    prompt: outgoing.prompt,
    detectTrigger: true,
  });
}
